import TwitchLogo from 'app/icons/stream_platform_twitch_filled.svg';
import KickLogo from 'app/icons/stream_platform_kick.svg';
import YoutubeLogo from 'app/icons/stream_platform_youtube.svg';
import BilibiliLogo from 'app/icons/stream_platform_bilibili_fill.svg';
import HuyaLogo from 'app/icons/stream_platform_huya.svg';
import SoopLogo from 'app/icons/stream_platform_soop.svg';
import DouyuLogo from 'app/icons/stream_platform_douyu.svg';
import FacebookLogo from 'app/icons/stream_platform_facebook.svg';
import TiktokLogo from 'app/icons/stream_platform_tiktok.svg';

const BETER_VIDEOGAME_SLUGS: VideogameSlug[] = [
  'e-soccer',
  'e-basketball',
  'e-cricket',
  'e-hockey',
  'e-tennis'
];

type StreamingPlatform = Exclude<StreamType, 'beter'>;

// Same host markers as controle (`ScheduledStreamCoverage.helper.ts`)
const STREAMING_PLATFORM_MARKERS: Record<StreamingPlatform, string[]> = {
  youtube: ['youtube', 'youtu.be'],
  twitch: ['twitch.'],
  kick: ['kick.'],
  bilibili: ['bilibili.'],
  huya: ['huya.'],
  soop: ['sooplive.'],
  douyu: ['douyu.'],
  facebook: ['facebook.', 'fb.'],
  tiktok: ['tiktok.']
};

// Controle's stream form also accepts URLs that start with `www.` and have no scheme.
const withUrlScheme = (url: string): string => {
  const trimmedUrl = url.trim();
  return /^[a-z][a-z\d+.-]*:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
};

const getUrlHost = (url: string): string => {
  try {
    return new URL(withUrlScheme(url)).hostname.toLowerCase();
  } catch {
    return url.toLowerCase();
  }
};

// `youtu.be/<id>` is YouTube's short link: rewrite it to `youtube.com/watch?v=<id>`.
// Only the id is kept, a trailing `?si=` share param would break the embed URL.
const toStandardYoutubeUrl = (url: string): string => {
  if (getUrlHost(url) !== 'youtu.be') {
    return url;
  }
  const id = new URL(withUrlScheme(url)).pathname.split('/').filter(Boolean)[0];
  return id ? `https://youtube.com/watch?v=${id}` : url;
};

// The Facebook player only plays one video: `/<page>/videos/<id>`, `/watch/?v=<id>`,
// `/watch/live/?v=<id>` or `/video.php?v=<id>`. A page URL (`/<page>`, `/<page>/live`) has no id.
const getFacebookVideoId = (url: string): string | null => {
  try {
    const { pathname, searchParams } = new URL(withUrlScheme(url));
    const segments = pathname.split('/').filter(Boolean);
    const videosIndex = segments.indexOf('videos');
    if (videosIndex !== -1) {
      return segments.slice(videosIndex + 1).find(segment => /^\d+$/.test(segment)) ?? null;
    }
    if (segments[0] === 'watch' || segments[0] === 'video.php') {
      return searchParams.get('v')?.trim() || null;
    }
    return null;
  } catch {
    return null;
  }
};

// The SOOP player lives on `play.` (`/<channel>/<broadcast id>`) or `ch.` (`/<channel>`) hosts,
// the channel id is always the first path segment.
const getSoopChannelId = (url: string): string | null => {
  try {
    const { hostname, pathname } = new URL(withUrlScheme(url));
    if (!/^(play|ch)\./i.test(hostname)) {
      return null;
    }
    return pathname.split('/').filter(Boolean)[0] ?? null;
  } catch {
    return null;
  }
};

const getStreamType = (stream: Stream, videogameSlug?: VideogameSlug): StreamType | undefined => {
  // Detect the platform from the URL host first: eBattle matches can carry a Twitch/YouTube
  // stream, and those players need the `parent`/`origin` params built in getStreamUrl.
  const host = getUrlHost(stream.raw_url);
  const platforms = Object.keys(STREAMING_PLATFORM_MARKERS) as StreamingPlatform[];
  const platform = platforms.find(platformKey =>
    STREAMING_PLATFORM_MARKERS[platformKey].some(marker => host.includes(marker))
  );
  if (platform) {
    return platform;
  }
  // Fallback for BETER-hosted players (no known platform in the URL).
  if (videogameSlug && BETER_VIDEOGAME_SLUGS.includes(videogameSlug)) {
    return 'beter';
  }
  return undefined;
};

const getStreamTypeIcon = (streamType: StreamType | undefined): SVG | null => {
  switch (streamType) {
    case 'twitch':
      return TwitchLogo;
    case 'youtube':
      return YoutubeLogo;
    case 'kick':
      return KickLogo;
    case 'bilibili':
      return BilibiliLogo;
    case 'huya':
      return HuyaLogo;
    case 'soop':
      return SoopLogo;
    case 'douyu':
      return DouyuLogo;
    case 'facebook':
      return FacebookLogo;
    case 'tiktok':
      return TiktokLogo;
    default:
      return null;
  }
};

const getStreamName = (stream: Stream, videogameSlug?: VideogameSlug): string | null => {
  const streamType = getStreamType(stream, videogameSlug);

  if (streamType === 'twitch') {
    const channelNameMatchArray = [...stream.raw_url.matchAll(/(twitch.tv\/)(.*)$/g)];
    if (channelNameMatchArray?.[0]?.[2]) {
      return channelNameMatchArray?.[0]?.[2].replace('_', ' ').toUpperCase().trim();
    }
  }

  if (streamType === 'kick') {
    const channelNameMatchArray = [...stream.raw_url.matchAll(/(kick.com\/)(.*)$/g)];
    if (channelNameMatchArray?.[0]?.[2]) {
      return channelNameMatchArray?.[0]?.[2].replace('_', ' ').trim() || null;
    }
  }

  return null;
};

const getStreamUrl = (
  stream: Stream | undefined,
  forcedOrigin: string | undefined, // keep this parameter for legacy reason
  forcedOrigins: string[] | undefined,
  videogameSlug?: VideogameSlug
): string | null => {
  if (!stream) {
    return null;
  }

  const streamType = getStreamType(stream, videogameSlug);

  const toHostname = (value: string): string => {
    try {
      return new URL(value).hostname;
    } catch {
      return value;
    }
  };

  const parents = [
    window.location.hostname,
    forcedOrigin ? toHostname(forcedOrigin) : undefined,
    ...(forcedOrigins ?? [])
    // Maybe this will be useful when all browsers support ancestorOrigins (damn you Firefox)
    // ...new Array(window.location.ancestorOrigins.length).fill(null).map((_, i) => {
    //   const ancestor = window.location.ancestorOrigins.item(i);
    //   if (ancestor) {
    //     return new URL(ancestor).hostname;
    //   }
    //   return null;
    // })
  ].filter(Boolean);

  if (streamType === 'twitch') {
    /* https://dev.twitch.tv/docs/embed/video-and-clips */
    return `${
      stream.embed_url ?? stream.raw_url.replace('www', 'player').replace('.tv/', '.tv/?channel=')
    }&allowfullscreen=true${parents.map(parent => `&parent=${parent}`).join('')}`;
  }

  if (streamType === 'youtube') {
    const rawUrl = toStandardYoutubeUrl(stream.raw_url);
    let part = '';
    if (stream.embed_url) {
      part = stream.embed_url;
    } else if (!rawUrl.includes('.com/watch?v=')) {
      return null;
    } else {
      part = rawUrl.replace('.com/watch?v=', '-nocookie.com/embed/');
    }
    /* https://developers.google.com/youtube/player_parameters */
    return `${part}?autoplay=1&origin=${window.location.hostname}`;
  }

  if (streamType === 'facebook') {
    if (stream.embed_url) {
      return stream.embed_url;
    }
    const videoId = getFacebookVideoId(stream.raw_url);
    if (!videoId) {
      return null;
    }
    // `video.php?v=<id>` is the video URL format given by the Facebook docs
    const href = encodeURIComponent(`https://www.facebook.com/video.php?v=${videoId}`);
    /* https://developers.facebook.com/docs/plugins/embedded-video-player/ */
    return `https://www.facebook.com/plugins/video.php?href=${href}&autoplay=true&allowfullscreen=true`;
  }

  if (streamType === 'soop') {
    if (stream.embed_url) {
      return stream.embed_url;
    }
    const channelId = getSoopChannelId(stream.raw_url);
    // The channel embed (the one SOOP gives in its `twitter:player` meta) plays whatever the
    // channel streams now, so it still works once the broadcast id in the raw url is outdated.
    return channelId ? `https://play.sooplive.com/${channelId}/embed` : null;
  }

  return stream.embed_url;
};

// Stream languages are ISO 639 language codes ("zh", "en"), but the flag files are named
// after ISO 3166 region codes ("cn", "us"). Many codes exist in both standards with a
// different meaning ("sv" is Swedish but also El Salvador), so resolve the likely region.
const getLanguageRegion = (language: string): string => {
  try {
    return new Intl.Locale(language).maximize().region?.toLowerCase() ?? language;
  } catch {
    return language;
  }
};

export { getStreamType, getStreamUrl, getStreamName, getStreamTypeIcon, getLanguageRegion };
